/**
 * Property Detail Page — KPI dashboard for a single property.
 *
 * Five KPI cards:
 *   1. # Tows in period (excludes courtesy tows — attribution gap)
 *   2. # No Charges in period
 *   3. # Refunds in period
 *   4. $ Spent on this account in period
 *   5. Profitability — placeholder until Matt's revenue equation lands
 *
 * Period switcher (MTD / Last Month / YTD / Custom) reuses the shared
 * <PeriodSelector /> so other dashboards can adopt the same toggle.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PeriodSelector from '../../components/PeriodSelector';
import AuditHistoryList from '../components/audit/AuditHistoryList';
import { formatMoney } from '../../utils/money';
import { formatDate } from '../../utils/dateUtils';

const PERIOD_LABELS = {
  mtd: 'Month to Date',
  last_month: 'Last Month',
  ytd: 'Year to Date',
  custom: 'Custom Range',
};

function fmtMoney(n) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCount(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function fmtDateRange(period) {
  if (!period?.start || !period?.end) return '';
  return `${formatDate(period.start)} – ${formatDate(period.end)}`;
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  // ISO might be "YYYY-MM-DD HH:MM:SS" (sqlite) or "YYYY-MM-DDT..." (Excel/Date)
  const safe = String(iso).replace(' ', 'T');
  const d = new Date(safe);
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString();
}

const STATUS_COLORS = {
  open: '#3b82f6', claimed: '#f59e0b', resolved: '#10b981',
  submitted: '#3b82f6', processing: '#f59e0b', completed: '#10b981',
  ready_for_payment: '#8b5cf6', denied: '#ef4444', already_released: '#6b7280',
  approved: '#10b981', pending: '#f59e0b', paid: '#10b981',
};

function StatusPill({ status }) {
  if (!status) return <span style={{ color: '#9ca3af' }}>—</span>;
  const color = STATUS_COLORS[status.toLowerCase()] || '#6b7280';
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
      backgroundColor: `${color}18`, color, textTransform: 'uppercase',
      letterSpacing: 0.4, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ── Grade panel helpers ────────────────────────────────────────
const GRADE_COLORS = {
  'A+': '#059669', 'A': '#059669', 'A-': '#059669',
  'B+': '#10b981', 'B': '#10b981', 'B-': '#10b981',
  'C+': '#f59e0b', 'C': '#f59e0b', 'C-': '#f59e0b',
  'D+': '#f97316', 'D': '#f97316', 'D-': '#f97316',
  'F': '#dc2626',
};
const STATUS_STYLES = {
  MAXED_OUT:        { label: 'MAXED OUT',        bg: '#dcfce7', color: '#166534' },
  UNDERPERFORMING:  { label: 'UNDERPERFORMING',  bg: '#fef3c7', color: '#92400e' },
  CRITICAL:         { label: 'CRITICAL',         bg: '#fee2e2', color: '#991b1b' },
  INSUFFICIENT_DATA:{ label: 'INSUFFICIENT DATA',bg: '#f3f4f6', color: '#6b7280' },
};
const KPI_LABELS = {
  profit_margin: 'Profit Margin',
  expense_ratio: 'Expense Ratio',
  true_no_charge_rate: 'True No Charge Rate',
  courtesy_tow_rate: 'Courtesy Tow Rate',
  tow_fee: 'Tow Fee',
};

function GradePill({ grade, size = 'md' }) {
  if (!grade) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  const color = GRADE_COLORS[grade] || '#6b7280';
  const sizes = {
    sm: { padding: '2px 8px', fontSize: 11, borderRadius: 4 },
    md: { padding: '4px 12px', fontSize: 13, borderRadius: 6 },
    xl: { padding: '12px 28px', fontSize: 56, borderRadius: 12, lineHeight: 1 },
  };
  return (
    <span style={{
      ...sizes[size],
      backgroundColor: size === 'xl' ? color : `${color}18`,
      color: size === 'xl' ? '#fff' : color,
      fontWeight: 800,
      letterSpacing: 0.5,
      display: 'inline-block',
    }}>
      {grade}
    </span>
  );
}

function fmtKpiValue(kpi, value) {
  if (value == null || !Number.isFinite(value)) return '—';
  if (kpi === 'tow_fee') return formatMoney(value);
  // Everything else is a rate
  return `${(value * 100).toFixed(2)}%`;
}

// ── Super-admin-only: enter the property's tow_fee + monthly Parking
// Snap counts. These are the two inputs that make the grade meaningful.
// Until Parking Snap API lands, these come from manual entry.
function TowDataAdminPanel({ property, kpis, onSaved }) {
  const [fee, setFee] = useState(property.tow_fee != null ? String(property.tow_fee) : '');
  const [feeSaving, setFeeSaving] = useState(false);
  const [counts, setCounts] = useState([]);
  const [draftYear, setDraftYear] = useState(new Date().getFullYear());
  const [draftMonth, setDraftMonth] = useState(new Date().getMonth() + 1);
  const [draftCount, setDraftCount] = useState('');
  const [busy, setBusy] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      setCounts(await api.getPropertyTowCounts(property.id));
    } catch { /* non-fatal */ }
  }, [property.id]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const saveFee = async () => {
    setFeeSaving(true);
    try {
      const val = fee.trim() === '' ? null : Number(fee);
      await api.setPropertyTowFee(property.id, val);
      onSaved && onSaved();
    } catch (e) {
      alert(e.message);
    } finally { setFeeSaving(false); }
  };

  const saveCount = async () => {
    if (!draftCount || !Number.isFinite(Number(draftCount))) {
      alert('Enter a tow count'); return;
    }
    setBusy(true);
    try {
      await api.setPropertyTowCount(property.id, draftYear, draftMonth, Number(draftCount));
      setDraftCount('');
      await loadCounts();
      onSaved && onSaved();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const removeCount = async (year, month) => {
    if (!window.confirm(`Remove tow count for ${year}-${String(month).padStart(2, '0')}?`)) return;
    setBusy(true);
    try {
      await api.deletePropertyTowCount(property.id, year, month);
      await loadCounts();
      onSaved && onSaved();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (
    <details style={{
      marginBottom: 18, padding: '12px 16px',
      backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
    }}>
      <summary style={{
        cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#92400e',
        textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        Grading Inputs (Super-admin)
      </summary>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {/* Tow Fee — per-property OVERRIDE. Most properties don't need this
            because the city default applies. Only set when this property
            has a negotiated fee that differs from the city default. */}
        <div style={{ padding: 12, backgroundColor: '#fff', borderRadius: 8, border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
            Tow Fee Override
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: '#6b7280' }}>$</span>
            <input type="number" step="0.01" min="0" value={fee}
              onChange={e => setFee(e.target.value)}
              placeholder={kpis?.tow_fee_city_default != null
                ? `${kpis.tow_fee_city_default} (city default)`
                : kpis?.tow_fee_core_default != null
                  ? `${kpis.tow_fee_core_default} (core market default)`
                  : 'optional'}
              className="sales-input" style={{ flex: 1, fontSize: 13 }} />
            <button onClick={saveFee} disabled={feeSaving} className="btn"
              style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}>
              {feeSaving ? '…' : 'Save'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            Leave blank to use the {kpis?.tow_fee_city_default != null
              ? `${property.city} city default (${formatMoney(kpis.tow_fee_city_default)})`
              : `core market default (${formatMoney(kpis?.tow_fee_core_default ?? 272)})`}.
            Only set when negotiated.
          </div>
        </div>

        {/* Add monthly tow count */}
        <div style={{ padding: 12, backgroundColor: '#fff', borderRadius: 8, border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
            Add / Update Monthly Tow Count
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input type="number" min="2000" max="2100" value={draftYear}
              onChange={e => setDraftYear(Number(e.target.value))}
              className="sales-input" style={{ width: 80, fontSize: 13 }} placeholder="Year" />
            <select value={draftMonth} onChange={e => setDraftMonth(Number(e.target.value))}
              className="sales-input" style={{ flex: 1, fontSize: 13 }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'long' })}
                </option>
              ))}
            </select>
            <input type="number" min="0" value={draftCount}
              onChange={e => setDraftCount(e.target.value)}
              className="sales-input" style={{ width: 80, fontSize: 13 }} placeholder="Tows" />
            <button onClick={saveCount} disabled={busy} className="btn"
              style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}>
              Save
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            From Parking Snap. Manual until API integration ships.
          </div>
        </div>
      </div>

      {/* Existing counts list */}
      {counts.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
            Entered Months
          </div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #fde68a' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Period</th>
                <th style={{ textAlign: 'right', padding: '4px 6px' }}>Tows</th>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Source</th>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Entered</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {counts.map(c => (
                <tr key={`${c.year}-${c.month}`} style={{ borderBottom: '1px solid #fef3c7' }}>
                  <td style={{ padding: '4px 6px' }}>{c.year}-{String(c.month).padStart(2, '0')}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{c.tow_count}</td>
                  <td style={{ padding: '4px 6px', color: '#9ca3af' }}>{c.source}</td>
                  <td style={{ padding: '4px 6px', color: '#9ca3af' }}>
                    {c.entered_by_name || '—'}{c.entered_at ? ` · ${String(c.entered_at).slice(0, 10)}` : ''}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <button onClick={() => removeCount(c.year, c.month)} disabled={busy} style={{
                      background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14,
                    }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}

function GradePanel({ grades, isSuperAdmin }) {
  if (grades.insufficient_data) {
    const status = STATUS_STYLES.INSUFFICIENT_DATA;
    return (
      <div style={{
        backgroundColor: '#fff', borderRadius: 12, padding: '24px 28px',
        border: '1px solid #e5e7eb', borderLeft: '6px solid #9ca3af',
        marginBottom: 18,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 56, fontWeight: 800, color: '#9ca3af', lineHeight: 1 }}>—</div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Property Grade
            </div>
            <div style={{
              display: 'inline-block', padding: '4px 10px', borderRadius: 4, marginTop: 6,
              backgroundColor: status.bg, color: status.color,
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            }}>{status.label}</div>
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
          Set this property's tow fee and enter Parking Snap monthly tow counts to compute a grade.
        </div>
      </div>
    );
  }

  const status = STATUS_STYLES[grades.status] || STATUS_STYLES.UNDERPERFORMING;
  const breakdown = grades.breakdown || {};
  const breakdownEntries = Object.entries(breakdown);

  return (
    <div style={{
      backgroundColor: '#fff', borderRadius: 12,
      border: '1px solid #e5e7eb', borderTop: `6px solid ${GRADE_COLORS[grades.final_grade] || '#6b7280'}`,
      marginBottom: 18, overflow: 'hidden',
    }}>
      {/* Hero strip — final grade + status + ceiling */}
      <div style={{
        display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap',
        padding: '22px 26px', borderBottom: '1px solid #f3f4f6',
      }}>
        <GradePill grade={grades.final_grade} size="xl" />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
            Property Grade
          </div>
          <div style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 4,
            backgroundColor: status.bg, color: status.color,
            fontSize: 12, fontWeight: 700, letterSpacing: 0.5, marginBottom: 8,
          }}>{status.label}</div>
          <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span>Market Ceiling: <strong style={{ color: '#111827' }}>{grades.market_ceiling}</strong></span>
            {isSuperAdmin && grades.raw_grade && grades.raw_grade !== grades.final_grade && (
              <span title="Raw cumulative before city compression. Super-admin only.">
                Raw: <strong style={{ color: '#111827' }}>{grades.raw_grade}</strong>
                {grades.raw_score != null && <span style={{ color: '#9ca3af' }}> ({grades.raw_score})</span>}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Breakdown grid */}
      <div style={{ padding: '16px 26px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
          Breakdown
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {breakdownEntries.map(([kpi, b]) => {
            const isPrimary = grades.primary_issue === kpi;
            const isSecondary = grades.secondary_issue === kpi;
            return (
              <div key={kpi} style={{
                padding: '10px 12px', borderRadius: 8,
                border: isPrimary ? '2px solid #dc2626' : isSecondary ? '2px solid #f59e0b' : '1px solid #f3f4f6',
                backgroundColor: isPrimary ? '#fef2f2' : isSecondary ? '#fffbeb' : '#fafbfc',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                    {KPI_LABELS[kpi] || kpi}
                  </span>
                  <GradePill grade={b.grade} size="sm" />
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  {fmtKpiValue(kpi, b.value)}
                  {b.weight ? <span style={{ marginLeft: 6, color: '#9ca3af' }}>· weight {Number(b.weight).toFixed(0)}%</span> : null}
                </div>
                {(isPrimary || isSecondary) && (
                  <div style={{
                    fontSize: 10, fontWeight: 700, marginTop: 4,
                    color: isPrimary ? '#dc2626' : '#92400e',
                    letterSpacing: 0.5, textTransform: 'uppercase',
                  }}>
                    {isPrimary ? 'Primary issue' : 'Secondary issue'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommended actions for the primary issue */}
      {grades.recommended_actions && grades.recommended_actions.length > 0 && (
        <div style={{
          padding: '14px 26px 18px', backgroundColor: '#fef2f2',
          borderTop: '1px solid #fecaca',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#991b1b',
            textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6,
          }}>
            Recommended Actions {grades.primary_issue && `· ${KPI_LABELS[grades.primary_issue] || grades.primary_issue}`}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#7f1d1d', lineHeight: 1.7 }}>
            {grades.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function ActivityCard({ title, count, emptyText, children }) {
  return (
    <div style={{
      backgroundColor: '#fff', borderRadius: 12, padding: '16px 18px',
      border: '1px solid #e5e7eb', flex: '1 1 320px', minWidth: 320,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12,
        paddingBottom: 8, borderBottom: '1px solid #f3f4f6',
      }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>{title}</h3>
        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>
          {count > 0 ? `Last ${count}` : ''}
        </span>
      </div>
      {count === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af', padding: '12px 0', fontStyle: 'italic' }}>
          {emptyText}
        </div>
      ) : children}
    </div>
  );
}

function KpiCard({ label, value, hint, accent, footnote }) {
  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: 12,
      padding: '20px 22px',
      border: '1px solid #e5e7eb',
      borderTop: `4px solid ${accent || '#3F66A0'}`,
      minWidth: 200,
      flex: '1 1 220px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: '#111827', lineHeight: 1.1 }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: '#6b7280' }}>{hint}</div>
      )}
      {footnote && (
        <div style={{ fontSize: 11, color: '#92400e', marginTop: 4, fontStyle: 'italic' }}>
          {footnote}
        </div>
      )}
    </div>
  );
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.permission_tier === 'super_admin';
  const [period, setPeriod] = useState({ kind: 'mtd' });
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { period: period.kind };
      if (period.kind === 'custom' && period.start && period.end) {
        params.start = period.start.slice(0, 10);
        params.end = period.end.slice(0, 10);
      }
      const r = await api.getPropertyKpis(id, params);
      setData(r);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, [id, period]);

  // Activity is independent of period — most-recent regardless of window.
  // Loaded once per property, re-fetched only when the property id changes.
  useEffect(() => {
    let cancelled = false;
    api.getPropertyActivity(id, { limit: 10 })
      .then(r => { if (!cancelled) setActivity(r); })
      .catch(() => { /* non-fatal — page still renders without activity */ });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;
  }
  if (error && !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>;
  }
  if (!data) return null;

  const { property, kpis, grades, notes } = data;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      {/* Back link */}
      <button
        onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}
      >
        ← Back
      </button>

      {/* Property header */}
      <div style={{
        backgroundColor: '#1a1a2e',
        color: '#fff',
        padding: '24px 28px',
        borderRadius: 12,
        marginBottom: 20,
        backgroundImage: 'linear-gradient(135deg, #1a1a2e 0%, #2d3561 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{
            padding: '3px 10px',
            backgroundColor: '#D4A843',
            color: '#1a1a2e',
            fontSize: 11,
            fontWeight: 700,
            borderRadius: 4,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}>
            {property.region}
          </span>
          {property.is_active === 0 && (
            <span style={{
              padding: '3px 10px',
              backgroundColor: '#ef4444',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 4,
              textTransform: 'uppercase',
            }}>
              Inactive
            </span>
          )}
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px', lineHeight: 1.2 }}>
          {property.name}
        </h1>
        <div style={{ fontSize: 14, color: '#cbd5e1' }}>
          {property.address}, {property.city} {property.zip}
        </div>
      </div>

      {/* Period selector */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginBottom: 16,
        flexWrap: 'wrap',
      }}>
        <PeriodSelector value={period} onChange={setPeriod} />
        <span style={{ fontSize: 12, color: '#9ca3af' }}>
          {PERIOD_LABELS[period.kind]} · {fmtDateRange(data.period)}
        </span>
      </div>

      {/* Tow data admin (super-admin only) — set fee + monthly Parking Snap counts.
          Re-runs the KPI/grade load when saved so the panel below reflects new inputs. */}
      {isSuperAdmin && <TowDataAdminPanel property={property} kpis={kpis} onSaved={load} />}

      {/* Grade Panel — above KPI tiles per spec */}
      {grades && <GradePanel grades={grades} isSuperAdmin={isSuperAdmin} />}

      {/* KPI tiles — kept as supporting detail under the grade. Profitability
          tile dropped because Profit Margin now lives in the Grade panel. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiCard
          label="Tows"
          value={fmtCount(kpis.parking_snap_tow_count)}
          hint={
            kpis.courtesy_tows_attributed > 0
              ? `Plus ${kpis.courtesy_tows_attributed} courtesy tow${kpis.courtesy_tows_attributed === 1 ? '' : 's'} (dispatch)`
              : 'From Parking Snap'
          }
          accent="#3F66A0"
        />
        <KpiCard
          label="No Charges"
          value={fmtCount(kpis.no_charges_count)}
          hint="Submitted no-charge requests"
          accent="#10b981"
        />
        <KpiCard
          label="Refunds"
          value={fmtCount(kpis.refunds_count)}
          hint="Submitted refund requests"
          accent="#f59e0b"
        />
        <KpiCard
          label="Money Spent"
          value={fmtMoney(kpis.money_spent)}
          hint="Expenses linked to this property"
          accent="#ef4444"
        />
        {kpis.tow_fee != null && (
          <KpiCard
            label="Tow Fee"
            value={fmtMoney(kpis.tow_fee)}
            hint={
              kpis.tow_fee_source === 'property' ? 'Property override'
                : kpis.tow_fee_source === 'city' ? `${property.city || 'City'} default`
                : kpis.tow_fee_source === 'core_market' ? 'Core market default'
                : 'Per-tow fee'
            }
            accent="#8b5cf6"
            footnote={
              kpis.tow_fee_source === 'property' && kpis.tow_fee_city_default != null
                ? `Default would be ${fmtMoney(kpis.tow_fee_city_default)}` : null
            }
          />
        )}
      </div>

      {/* Notes / accuracy disclaimers */}
      {notes && notes.length > 0 && (
        <div style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 10,
          padding: '12px 16px',
          fontSize: 12,
          color: '#92400e',
          marginBottom: 20,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Notes
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            {notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      {/* Recent activity — three columns, last 10 each. Loaded
          independently of the period selector since it shows
          most-recent-regardless-of-window. */}
      {activity && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
            Recent Activity
          </h2>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <ActivityCard
              title="Courtesy Tows"
              count={activity.calls.length}
              emptyText="No courtesy tows attributed to this property yet."
            >
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <tbody>
                  {activity.calls.map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 4px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                        {fmtDateShort(c.created_at)}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <Link to={`/dispatch/${c.id}`} style={{ color: '#3F66A0', fontWeight: 600, textDecoration: 'none' }}>
                          {c.call_type || 'Call'} #{c.id}
                        </Link>
                        {c.vehicle_info && (
                          <div style={{ color: '#9ca3af', fontSize: 11 }}>{c.vehicle_info}</div>
                        )}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                        <StatusPill status={c.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ActivityCard>

            <ActivityCard
              title="Requests"
              count={activity.requests.length}
              emptyText="No refunds or no-charges yet."
            >
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <tbody>
                  {activity.requests.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 4px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                        {fmtDateShort(r.created_at)}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <Link to={`/sales/requests/${r.type}/${r.id}`} style={{ color: '#3F66A0', fontWeight: 600, textDecoration: 'none' }}>
                          {r.type_icon || ''} {r.type_label || r.type} #{r.id}
                        </Link>
                        {r.amount_requested && (
                          <div style={{ color: '#9ca3af', fontSize: 11 }}>{fmtMoney(r.amount_requested)}</div>
                        )}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                        <StatusPill status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ActivityCard>

            <ActivityCard
              title="Expenses"
              count={activity.expenses.length}
              emptyText="No expenses linked to this property yet."
            >
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <tbody>
                  {activity.expenses.map(e => {
                    const splitCount = Number(e.linked_property_count) || 1;
                    const isSplit = splitCount > 1;
                    return (
                      <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 4px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                          {fmtDateShort(e.expense_date || e.created_at)}
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          <span style={{ fontWeight: 600, color: '#111827' }}>
                            {fmtMoney(e.amount)}
                          </span>
                          {isSplit && (
                            <span title={`${fmtMoney(e.full_amount)} split across ${splitCount} properties`}
                              style={{ marginLeft: 6, fontSize: 10, color: '#92400e', fontWeight: 700 }}>
                              1/{splitCount} of {fmtMoney(e.full_amount)}
                            </span>
                          )}
                          <div style={{ color: '#9ca3af', fontSize: 11 }}>
                            {e.submitter_name || '—'}{e.category && e.category !== 'sales' ? ` · ${e.category}` : ''}
                          </div>
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                          <StatusPill status={e.status} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ActivityCard>
          </div>
        </div>
      )}

      {/* Audit history for this property (only when property has a uuid —
          audits join on properties.uuid, not the integer id). */}
      {property.uuid && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
            Audit History
          </h2>
          <AuditHistoryList propertyId={property.uuid} limit={20} />
        </div>
      )}

      {/* What's still missing — visible TODO so users know where this is going */}
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: 10,
        padding: '16px 20px',
        fontSize: 13,
        color: '#6b7280',
        border: '1px dashed #e5e7eb',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: '#374151' }}>Coming next</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>Sales rep assignment + "My Properties" landing page</li>
            <li>Profitability calculator (once revenue equation lands)</li>
            <li>Courtesy tow attribution (currently undercounts tow volume)</li>
        </ul>
      </div>
    </div>
  );
}
